import React from 'react';
import { motion } from 'framer-motion';
import { Shield, CheckCircle, Wrench, Settings } from 'lucide-react';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import safetyValveImage from '../../../images/herosection/safetyvalve.jpg';
import safetyValve1 from '../../../images/SafetyValve/SafetyValve1.png';
import safetyValve2 from '../../../images/SafetyValve/SafetyValve2.png';
import breatherValve1 from '../../../images/BreatherValve/BreatherValve1.png';
import EnquiryForm from '../../../components/EnquiryForm/EnquiryForm';
import './SafetyValveService.css';

const SafetyValveService = () => {
  const safetyValveFeatures = [
    'All brands and sizes supported',
    'Shop & field repair services',
    'High and low pressure valves',
    '24/7 technical support',
    'Extensive manufacturer specifications library',
    'Online pressure setting',
    'Spare supply',
    'Reconditioning/refurbishment work'
  ];
  const breatherValveFeatures = [
    'All brands and sizes supported',
    'Tank protection and vacuum relief',
    'Shop & field repair services',
    '24/7 technical support',
    'Online pressure setting',
    'Spare supply',
    'Reconditioning/refurbishment work'
  ];

  const process = [
    {
      step: '01',
      title: 'Assessment & Inspection',
      description: 'Thorough evaluation of valve condition and performance requirements'
    },
    {
      step: '02',
      title: 'Disassembly & Cleaning',
      description: 'Complete disassembly and professional cleaning of all components'
    },
    {
      step: '03',
      title: 'Repair & Refurbishment',
      description: 'Expert repair, replacement of worn parts, and refurbishment'
    },
    {
      step: '04',
      title: 'Testing & Calibration',
      description: 'Comprehensive testing and precise calibration to specifications'
    },
    {
      step: '05',
      title: 'Documentation & Certification',
      description: 'Complete documentation and certification of work performed'
    }
  ];


  return (
    <div className="safety-valve-service-page">
      {/* Hero Section */}
      <section className="safety-valve-hero" style={{ backgroundImage: `url(${safetyValveImage})` }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="safety-valve-hero-content"
          >
            <Breadcrumb />
            <h1 className="safety-valve-hero-title">
              Safety Valve & Breather Valves <span className="safety-valve-hero-title-highlight">Service & Spares</span>
            </h1>
            <p className="safety-valve-hero-description">
              Comprehensive repair and maintenance for all brands and sizes of safety valves and breather valves. 
              Ensuring your critical safety systems operate reliably and efficiently.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 1: Safety Valve */}
      <section className="safety-valve-overview">
        <div className="container-custom">
          <div className="safety-valve-overview-grid">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="safety-valve-overview-content"
            >
              <h2 className="safety-valve-overview-title">Why Safety Valve Maintenance is Critical</h2>
              <p className="safety-valve-overview-description">
                Safety valves are the last line of defense in protecting your facility from overpressure conditions. 
                Regular maintenance and proper repair ensure these critical components function reliably when needed most.
              </p>
              <div className="safety-valve-features">
                {safetyValveFeatures.map((feature, index) => (
                  <div key={index} className="safety-valve-feature-item">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="safety-valve-overview-visual"
            >
              <div className="safety-valve-image-gallery">
                {[
                  { src: safetyValve1, alt: 'Safety Valve Service', title: 'Professional Service' },
                  { src: safetyValve2, alt: 'Safety Valve Expertise', title: 'Expert Repair' }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="safety-valve-image-card"
                  >
                    <div className="safety-valve-image-card-inner">
                      <img src={item.src} alt={item.alt} />
                      <div className="safety-valve-image-card-overlay">
                        <h4>{item.title}</h4>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 2: Breather Valve */}
      <section className="safety-valve-overview safety-valve-overview-alt">
        <div className="container-custom">
          <div className="safety-valve-overview-grid safety-valve-overview-grid-reverse">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="safety-valve-overview-content"
            >
              <h2 className="safety-valve-overview-title">Why Breather Valve Maintenance Matters</h2>
              <p className="safety-valve-overview-description">
                Breather valves protect tanks and vessels from overpressure and vacuum by allowing air to flow in and out as needed. 
                Proper maintenance ensures reliable tank protection and prevents costly damage or safety incidents.
              </p>
              <div className="safety-valve-features">
                {breatherValveFeatures.map((feature, index) => (
                  <div key={index} className="safety-valve-feature-item">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="safety-valve-overview-visual"
            >
              <div className="safety-valve-image-gallery safety-valve-image-gallery--single">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="safety-valve-image-card"
                >
                  <div className="safety-valve-image-card-inner">
                    <img src={breatherValve1} alt="Breather Valve Service" />
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Equipment Showcase */}
      <section className="safety-valve-equipment">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="safety-valve-equipment-header"
          >
            <h2 className="safety-valve-equipment-title">Safety Valve Equipment</h2>
            <p className="safety-valve-equipment-description">
              Professional equipment and tools for comprehensive safety valve services
            </p>
          </motion.div>
          
          <div className="safety-valve-equipment-grid">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="safety-valve-equipment-item"
            >
              <div className="safety-valve-equipment-image">
                <div className="safety-valve-equipment-placeholder testing-equipment">
                  <Shield className="w-12 h-12 text-white" />
                </div>
              </div>
              <h3 className="safety-valve-equipment-name">Testing Equipment</h3>
              <p className="safety-valve-equipment-description">Advanced testing equipment for accurate pressure and flow measurements</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="safety-valve-equipment-item"
            >
              <div className="safety-valve-equipment-image">
                <div className="safety-valve-equipment-placeholder repair-tools">
                  <Wrench className="w-12 h-12 text-white" />
                </div>
              </div>
              <h3 className="safety-valve-equipment-name">Repair Tools</h3>
              <p className="safety-valve-equipment-description">Specialized tools for precision repair and maintenance work</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="safety-valve-equipment-item"
            >
              <div className="safety-valve-equipment-image">
                <div className="safety-valve-equipment-placeholder calibration-tools">
                  <Settings className="w-12 h-12 text-white" />
                </div>
              </div>
              <h3 className="safety-valve-equipment-name">Calibration Tools</h3>
              <p className="safety-valve-equipment-description">Precision calibration equipment for accurate valve settings</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="safety-valve-process">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="safety-valve-process-header"
          >
            <h2 className="safety-valve-process-title">Our Service Process</h2>
            <p className="safety-valve-process-description">
              A systematic approach to ensure quality and reliability
            </p>
          </motion.div>
          
          <div className="safety-valve-process-grid">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="safety-valve-process-step"
              >
                <div className="safety-valve-process-number">{step.step}</div>
                <h3 className="safety-valve-process-step-title">{step.title}</h3>
                <p className="safety-valve-process-step-description">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Section - Generic */}
      <section className="safety-valve-brands">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="safety-valve-brands-header"
          >
            <h2 className="safety-valve-brands-title">We Work With All Brands</h2>
            <p className="safety-valve-brands-description">
              We service all major safety valve and breather valve manufacturers. Our team has the expertise and specifications to repair and maintain equipment from any brand.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="safety-valve-cta">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="safety-valve-cta-content"
          >
            <h2 className="safety-valve-cta-title">Ready to Schedule Your Safety Valve or Breather Valve Service?</h2>
            <p className="safety-valve-cta-description">
              Get a free consultation and quote by filling out the form below
            </p>
            
            <div className="safety-valve-cta-form">
              <EnquiryForm serviceName="Safety Valve & Breather Valves" />
            </div>
            
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SafetyValveService;
